/**
 * components/wizard/KlicklisteLeiste.jsx — 🆕 26.09.2026 · Auftrag 29, R3.
 *
 * Die Leiste über dem Anker-Sammelmodus, wenn die URL `liste` und `nr`
 * trägt. Sie zeigt den Punkt und seinen Stand (gesetzt / unklickbar /
 * offen, vom Backend abgeleitet), führt zum vorherigen und zum nächsten
 * offenen Punkt — auch in ein anderes Video, über die Route — und merkt
 * „unklickbar" mit Notiz. Die Navigation selbst steht als reine Funktion
 * in `lib/klickliste.js`.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Crosshair } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  fehlerText, getKlickliste, nimmUnklickbarZurueck, setzeUnklickbar,
} from "@/lib/api";
import {
  fortschrittText, naechsterOffener, punktZu, sprungPfad, vorheriger,
} from "@/lib/klickliste";

const STAND_KLASSE = {
  gesetzt: "text-emerald-300 border-emerald-400/50 bg-emerald-400/10",
  unklickbar: "text-amber-300 border-amber-300/50 bg-amber-300/10",
  offen: "text-zinc-300 border-zinc-400/50 bg-zinc-400/10",
};

export default function KlicklisteLeiste({
  name, nr, sequenceId, dirty, onVerwerfen, onZielbild,
}) {
  const navigate = useNavigate();
  const [daten, setDaten] = useState(null);
  const [fehler, setFehler] = useState("");
  const [notiz, setNotiz] = useState("");
  const [busy, setBusy] = useState(false);

  const laden = useCallback(async () => {
    try {
      setDaten(await getKlickliste(name));
      setFehler("");
    } catch (e) {
      setFehler(fehlerText(e) || "Klickliste nicht lesbar");
    }
  }, [name]);

  useEffect(() => { laden(); }, [laden]);
  // Nach jedem Speichern/Löschen eines Ankers meldet AnchorCollectMode
  // `anchors-changed` — der Stand ist abgeleitet und wird neu geholt.
  useEffect(() => {
    const h = () => laden();
    globalThis.addEventListener("anchors-changed", h);
    return () => globalThis.removeEventListener("anchors-changed", h);
  }, [laden]);
  useEffect(() => { setNotiz(""); }, [nr]);

  const punkte = daten?.punkte || [];
  const punkt = punktZu(punkte, nr);

  const geheZu = (p, hinweis) => {
    if (!p) return;
    if (dirty && !globalThis.confirm(
      `Ungespeicherte Linien in diesem Bild. Verwerfen und zu Punkt ${p.nr} wechseln?`)) {
      return;
    }
    if (dirty) onVerwerfen?.();
    if (hinweis) toast.info(hinweis);
    navigate(sprungPfad({ seq: p.seq, t: p.t_s, liste: name, nr: p.nr }));
  };

  const naechster = () => {
    const n = naechsterOffener(punkte, nr);
    if (!n) {
      toast.info("Keine offenen Punkte mehr in der Liste.");
      return;
    }
    geheZu(n.punkt, n.vonVorn
      ? `Ende der Liste — weiter von vorn bei Punkt ${n.punkt.nr}`
      : null);
  };

  const unklickbar = async () => {
    setBusy(true);
    try {
      await setzeUnklickbar(name, nr, notiz);
      toast.success(`Punkt ${nr} als unklickbar notiert`);
      await laden();
    } catch (e) {
      toast.error(fehlerText(e) || "Konnte nicht speichern");
    } finally {
      setBusy(false);
    }
  };

  const zuruecknehmen = async () => {
    setBusy(true);
    try {
      await nimmUnklickbarZurueck(name, nr);
      toast.success(`Punkt ${nr}: „unklickbar" zurückgenommen`);
      await laden();
    } catch (e) {
      toast.error(fehlerText(e) || "Konnte nicht zurücknehmen");
    } finally {
      setBusy(false);
    }
  };

  if (fehler) {
    return (
      <div data-testid="klickliste-leiste" className="border border-rose-400/50 bg-rose-400/10 text-rose-200 rounded-sm px-4 py-2 font-mono text-[11px] mb-3">
        Klickliste {name}: {fehler}
      </div>
    );
  }
  if (!daten) {
    return (
      <div data-testid="klickliste-leiste" className="border border-border rounded-sm px-4 py-2 font-mono text-[11px] text-muted-foreground mb-3">
        Klickliste wird geladen …
      </div>
    );
  }
  if (!punkt) {
    return (
      <div data-testid="klickliste-leiste" className="border border-rose-400/50 bg-rose-400/10 text-rose-200 rounded-sm px-4 py-2 font-mono text-[11px] mb-3">
        Klickliste: Punkt {nr} gibt es nicht (1 bis {daten.n}).
      </div>
    );
  }

  const fremdesVideo = punkt.seq !== sequenceId;
  const uk = punkt.unklickbar;

  return (
    <div data-testid="klickliste-leiste" className="border border-cyan-500/40 bg-cyan-500/5 rounded-sm px-4 py-3 mb-3 space-y-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[11px]">
        <span className="uppercase tracking-widest text-cyan-300">Klickliste</span>
        <span data-testid="klickliste-kopf">
          Punkt {punkt.nr} von {daten.n} · {punkt.video} · Shot {punkt.shot} · {punkt.t_mmss} · Stufe {punkt.stufe} · Abstand zur Referenz {punkt.abstand_s} s
        </span>
        <span
          data-testid="klickliste-stand"
          className={`border rounded-sm px-2 py-0.5 uppercase tracking-widest ${STAND_KLASSE[punkt.stand] || ""}`}
        >
          {punkt.stand}
        </span>
        {punkt.stand === "gesetzt" && punkt.anker && (
          <span className="text-muted-foreground">
            Anker {String(punkt.anker.id).slice(0, 8)} · {punkt.anker.abstand_s} s neben dem Zielbild
          </span>
        )}
        {uk && (
          <span className="text-amber-200/80">
            unklickbar notiert{uk.notiz ? `: „${uk.notiz}"` : ""} · {uk.zeit}
          </span>
        )}
      </div>
      {fremdesVideo && (
        <div className="font-mono text-[11px] text-rose-300">
          Dieser Punkt gehört zu einem anderen Video.{" "}
          <button type="button" className="underline" onClick={() => geheZu(punkt)}>
            Zum Video des Punkts
          </button>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          data-testid="klickliste-vorher" variant="outline" size="sm"
          className="font-mono text-[10px]"
          disabled={!vorheriger(punkte, nr)}
          onClick={() => geheZu(vorheriger(punkte, nr))}
        >
          <ChevronLeft className="w-3.5 h-3.5 mr-1" /> vorheriger
        </Button>
        <Button
          data-testid="klickliste-naechster" variant="outline" size="sm"
          className="font-mono text-[10px]" onClick={naechster}
        >
          nächster offener <ChevronRight className="w-3.5 h-3.5 ml-1" />
        </Button>
        <Button
          data-testid="klickliste-zielbild" variant="outline" size="sm"
          className="font-mono text-[10px]" disabled={fremdesVideo}
          onClick={() => onZielbild?.(punkt.t_s)}
        >
          <Crosshair className="w-3.5 h-3.5 mr-1" /> zurück zum Zielbild
        </Button>
        {uk ? (
          <Button
            data-testid="klickliste-zuruecknehmen" variant="outline" size="sm"
            className="font-mono text-[10px] border-amber-300/50 text-amber-300"
            disabled={busy} onClick={zuruecknehmen}
          >
            „unklickbar" zurücknehmen
          </Button>
        ) : (
          <>
            <input
              data-testid="klickliste-notiz"
              value={notiz}
              onChange={(e) => setNotiz(e.target.value)}
              placeholder="Notiz (warum nicht klickbar)"
              maxLength={500}
              className="h-8 w-64 rounded-sm border border-border bg-background px-2 font-mono text-[11px]"
            />
            <Button
              data-testid="klickliste-unklickbar" variant="outline" size="sm"
              className="font-mono text-[10px] border-amber-300/50 text-amber-300"
              disabled={busy} onClick={unklickbar}
            >
              unklickbar
            </Button>
          </>
        )}
      </div>
      <div data-testid="klickliste-fortschritt" className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        {fortschrittText(daten.fortschritt)}
      </div>
    </div>
  );
}
